import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { DiaryContextType } from "@/context/DiaryContext";
import { useDiaryContext } from "@/hooks/useDiaryContext";
import { UndoableAssignment } from "@/components/Pages/Diary/SpaceScanner/SpaceScanner.types";
import {
  UndoOptimisationResult,
  undoOptimisation
} from "@/components/Pages/Diary/SpaceScanner/undoOptimisation";

export type SpaceOptimiserRunStatus = "applied" | "undone" | "dismissed";

export interface SpaceOptimiserRun {
  status: SpaceOptimiserRunStatus;
  assignments: UndoableAssignment[];
  venueId: string;
  dateKey: string;
  undoFailureCount: number;
}

type SpaceOptimiserContextType = {
  run: SpaceOptimiserRun | null;
  isUndoing: boolean;
  recordAppliedRun: (assignments: UndoableAssignment[]) => void;
  undoRun: (options?: {
    onProgress?: (processed: number) => void;
  }) => Promise<UndoOptimisationResult>;
  dismissRun: () => void;
};

const emptyUndoResult: UndoOptimisationResult = {
  revertedBookingIds: [],
  failures: []
};

const SpaceOptimiserContext = createContext<SpaceOptimiserContextType>({
  run: null,
  isUndoing: false,
  recordAppliedRun: () => {},
  undoRun: async () => emptyUndoResult,
  dismissRun: () => {}
});

type SpaceOptimiserProviderProps = {
  children: ReactNode;
};

export const SpaceOptimiserProvider = ({
  children
}: SpaceOptimiserProviderProps) => {
  const diaryContext: DiaryContextType = useDiaryContext();
  const diaryContextRef = useRef(diaryContext);
  diaryContextRef.current = diaryContext;
  const { selectedVenue, venueConfig, date } = diaryContext;
  const venueId = selectedVenue ?? venueConfig?._id;
  const dateKey = date.toISOString().split("T")[0];
  const [run, setRun] = useState<SpaceOptimiserRun | null>(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const isUndoingRef = useRef(false);

  const recordAppliedRun = useCallback(
    (assignments: UndoableAssignment[]) => {
      if (assignments.length === 0) {
        setRun(null);
        return;
      }

      setRun({
        status: "applied",
        assignments,
        venueId: venueId ?? "",
        dateKey,
        undoFailureCount: 0
      });
    },
    [venueId, dateKey]
  );

  const dismissRun = useCallback(() => {
    setRun((currentRun) =>
      currentRun
        ? {
            ...currentRun,
            status: "dismissed",
            assignments: [],
            undoFailureCount: 0
          }
        : null
    );
  }, []);

  const undoRun = useCallback(
    async ({
      onProgress
    }: { onProgress?: (processed: number) => void } = {}) => {
      if (run?.status !== "applied" || isUndoingRef.current) {
        return emptyUndoResult;
      }

      isUndoingRef.current = true;
      setIsUndoing(true);

      try {
        const result = await undoOptimisation({
          assignments: run.assignments,
          diaryContext: diaryContextRef.current,
          onProgress
        });

        if (result.failures.length === 0) {
          setRun((currentRun) =>
            currentRun === run
              ? {
                  ...run,
                  status: "undone",
                  assignments: [],
                  undoFailureCount: 0
                }
              : currentRun
          );
        } else {
          setRun((currentRun) =>
            currentRun === run
              ? {
                  ...run,
                  assignments: result.failures.map(
                    ({ assignment }) => assignment
                  ),
                  undoFailureCount: result.failures.length
                }
              : currentRun
          );
        }

        return result;
      } finally {
        isUndoingRef.current = false;
        setIsUndoing(false);
      }
    },
    [run]
  );

  useEffect(() => {
    if (
      run &&
      venueId !== undefined &&
      dateKey !== undefined &&
      (venueId !== run.venueId || dateKey !== run.dateKey)
    ) {
      setRun(null);
    }
  }, [run, venueId, dateKey]);

  const contextValue = useMemo(
    () => ({
      run,
      isUndoing,
      recordAppliedRun,
      undoRun,
      dismissRun
    }),
    [run, isUndoing, recordAppliedRun, undoRun, dismissRun]
  );

  return (
    <SpaceOptimiserContext.Provider value={contextValue}>
      {children}
    </SpaceOptimiserContext.Provider>
  );
};

export const useSpaceOptimiserContext = () =>
  React.useContext(SpaceOptimiserContext);
